import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

export default function AtlasTroubleshooting() {
  return (
    <>
      <NextSeo
        title="Troubleshooting Atlas | Chat.co"
        description="Handle unknown outcomes, interruptions, availability and limits."
      />
      <DocsArticleLayout
        category="Atlas"
        categoryColor="text-blue-700"
        title="Troubleshooting Atlas"
        description="Check what happened before deciding whether another attempt is safe."
        readingTime="5 min read"
        tableOfContents={[
          { id: 'unknown', title: 'Unknown outcomes' },
          { id: 'interrupted', title: 'Interrupted conversations' },
          { id: 'limits', title: 'Access, limits and unavailable actions' },
          { id: 'support', title: 'Prepare a support request' },
        ]}
        prevArticle={{
          title: 'Atlas roles, privacy and key delivery',
          href: PageLink.docsAtlasPrivacy,
        }}
        nextArticle={{ title: 'Atlas owner guide', href: PageLink.docsAtlas }}>
        <section id="unknown">
          <h2>Unknown outcomes</h2>
          <DocsCallout variant="warning" title="Do not repeat an uncertain effect">
            Do not blindly retry payments, purchases, sends, key creation or data changes when the
            outcome is unknown. A timeout, missing reply or disconnected tab does not prove that
            nothing happened.
          </DocsCallout>
          <ol className="list-decimal space-y-2 pl-6">
            <li>Open the existing task and inspect its latest status and result evidence.</li>
            <li>
              Check the relevant destination: bot settings or sources, message destination, or
              billing and checkout status.
            </li>
            <li>
              If confirmation remains unavailable, contact support with the task context and time.
              Keep the outcome as unknown until it can be established.
            </li>
            <li>
              Only request another effect once you know it did not happen or have clear recovery
              guidance. Review the new effect separately.
            </li>
          </ol>
          <p>
            Sending or Submitted means a decision is still awaiting confirmation. A prepared
            checkout link is not a successful payment. Do not create another purchase to resolve an
            uncertain payment.
          </p>
        </section>
        <section id="interrupted">
          <h2>Interrupted conversations</h2>
          <p>
            Return to the existing task after a connection interruption. Read new status and
            evidence before submitting another request. Leaving Atlas or switching to Classic does
            not itself cancel work already accepted.
          </p>
          <p>
            If the task offers Pause or Stop, read the control and subsequent status. A stopped task
            does not undo earlier effects. Use Undo only when it is offered for the specific
            completed change and its window remains open.
          </p>
          <p>
            A Retry control is not proof that an external effect is safe to repeat. For uncertain
            writes or spending, follow the checks above. If an expired review offers Review again or
            Ask again, it requests a new review rather than reusing approval.
          </p>
        </section>
        <section id="limits">
          <h2>Access, limits and unavailable actions</h2>
          <p>
            Atlas is available to enrolled accounts with the required permissions. Enrollment does
            not enable every capability. If the <Link href="/home">Atlas workspace</Link> is
            unavailable, check the signed-in account and permissions, then continue in Classic or
            ask support about access.
          </p>
          <p>
            If onboarding, refresh, a routine or an integration is unavailable, do not assume that
            requesting it in chat will enable it. Use the existing classic workflow where available.
            A connected account does not guarantee that every action for that integration is
            supported.
          </p>
          <p>
            When a usage limit or quota is reported, read the displayed usage, limit and reset or
            billing information. Missing usage is unknown, not unlimited. Do not infer a price from
            an estimate or bypass a limit by repeating the request. Review any offered checkout
            separately.
          </p>
          <details>
            <summary>My answer has weak or missing evidence</summary>
            <p>
              Check source freshness, training status and the passage behind each citation. Compare
              test invocation success with the separate quality verdict. Follow the{' '}
              <Link href={PageLink.docsAtlasKnowledge}>knowledge and quality guide</Link> before
              changing sources.
            </p>
          </details>
        </section>
        <section id="support">
          <h2>Prepare a support request</h2>
          <p>
            Include the account and bot context, the visible task title, approximate time and
            timezone, the step you took, and the exact error wording. State whether the outcome is
            unknown and what destination checks you made. Remove credentials and unnecessary
            personal data from screenshots.
          </p>
          <p>
            <Link href={PageLink.contactUs}>Contact support about Atlas</Link> or return to the{' '}
            <Link href={PageLink.docsAtlas}>Atlas owner guide</Link>.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
}
